import useStore from '@store/store';
import classNames from 'classnames';
import React from 'react';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { getChartColors, getSlicePath } from 'utilities/helper';

import Tooltip from './Tooltip';

const colors = getChartColors();

interface Props {
  region: string;
  data: [];
  max?: number;
}
interface TooltipItem {
  label: string;
  value: number;
  color: string;
}

const RadialChart: React.FC<Props> = ({ region = '', data = [], max = 20 }) => {
  const angle = 360 / data.length;

  const [tooltipTitle, setTooltipTitle] = useState('');
  const [tooltipItems, setTooltipItems] = useState<TooltipItem[]>([]);

  const mask = Date.now();

  function getRadius(score = 0) {
    return 43.75 * (score / max) + 6.25;
  }

  const { ref, inView } = useInView({
    threshold: 0.75,
  });

  const { generatingPDF } = useStore();

  return (
    <div
      className="relative pointer-events-none text-xs md:text-sm text-center"
      ref={ref}
    >
      {data.map(({ label }, index) => (
        <div
          className={classNames(
            'absolute bottom-1/2 left-1/2 h-[45%] origin-bottom'
          )}
          style={{
            transform: `rotate(${angle * index + angle / 2}deg)`,
          }}
          key={label}
        >
          <span
            className="absolute bottom-full left-0 origin-bottom"
            style={{
              transform: `rotate(${-angle * index - angle / 2}deg)`,
            }}
          >
            <span
              className={classNames(
                'absolute top-0 md:!inset-0 w-40 -translate-x-1/2 -translate-y-1/2',
                {
                  'top-[30px]': index === 5,
                  'left-[40px]': index === 4,
                  '-top-[20px]': index === 2,
                  '-left-[60px]': index === 1,
                }
              )}
            >
              {label}
            </span>
          </span>
        </div>
      ))}
      <div className="group/tooltip rounded-full overflow-hidden">
        <svg viewBox="-15 -15 130 130">
          <mask id={`mask-${mask}`}>
            <rect x="-15" y="-15" width="130" height="130" fill="white" />
            <circle cx="50" cy="50" r="6.25" fill="black" />
          </mask>
          <g mask={`url(#mask-${mask})`}>
            <circle
              cx="50"
              cy="50"
              r="49.9"
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.3"
              strokeWidth="0.2"
              strokeDasharray="0.5"
            />
            {data.map(({ label, scores }, index) => (
              <g transform={`rotate(${angle * index} 50 50)`} key={label}>
                <line
                  x1="50"
                  y1="50"
                  x2="50"
                  y2="-3.5"
                  stroke="white"
                  strokeOpacity="0.3"
                  strokeWidth="0.2"
                />
                <path
                  className="opacity-0 transition-opacity duration-200 ease-linear hover:opacity-100 pointer-events-auto"
                  d={getSlicePath(65, angle)}
                  fill="white"
                  fillOpacity="0.1"
                  onPointerEnter={() => {
                    setTooltipTitle(label);
                    setTooltipItems([
                      {
                        label: 'Your overall score',
                        value: scores[0],
                        color: colors[0],
                      },
                      {
                        label: 'Global (average)',
                        value: scores[1],
                        color: colors[1],
                      },
                      {
                        label: `Regional (average): ${region}`,
                        value: scores[2],
                        color: colors[2],
                      },
                      {
                        label: 'Market leaders (average)',
                        value: scores[3],
                        color: colors[3],
                      },
                    ]);
                  }}
                />
                <path
                  className="transition-all duration-200 ease-out"
                  d={getSlicePath(
                    generatingPDF || inView ? getRadius(scores[0]) : 0,
                    angle
                  )}
                  fill={colors[0]}
                  stroke="white"
                  strokeWidth="0.1"
                  opacity="0.8"
                  style={{
                    transitionDelay: `${index * 125}ms`,
                  }}
                />
                <path
                  transform={`rotate(${(angle / 4) * 1 - 2} 50 50)`}
                  className="transition-all duration-200 ease-out"
                  d={getSlicePath(
                    generatingPDF || inView ? getRadius(scores[1]) : 0,
                    4
                  )}
                  fill={colors[1]}
                  style={{
                    transitionDelay: `${index * 125 + 125}ms`,
                  }}
                />
                <path
                  transform={`rotate(${(angle / 4) * 2 - 2} 50 50)`}
                  className="transition-all duration-200 ease-out"
                  d={getSlicePath(
                    generatingPDF || inView ? getRadius(scores[2]) : 0,
                    4
                  )}
                  fill={colors[2]}
                  style={{
                    transitionDelay: `${index * 125 + 250}ms`,
                  }}
                />
                <path
                  transform={`rotate(${(angle / 4) * 3 - 2} 50 50)`}
                  className="transition-all duration-200 ease-out"
                  d={getSlicePath(
                    generatingPDF || inView ? getRadius(scores[3]) : 0,
                    4
                  )}
                  fill={colors[3]}
                  style={{
                    transitionDelay: `${index * 125 + 375}ms`,
                  }}
                />
              </g>
            ))}
            <circle
              cx="50"
              cy="50"
              r="6.25"
              stroke="white"
              strokeOpacity="0.3"
              strokeWidth="0.4"
            />
          </g>
          <g
            fill="currentColor"
            textAnchor="middle"
            style={{
              fontSize: '20%',
            }}
          >
            <text x="50" y="51">
              0
            </text>
            <text x="50" y="-5">
              {max}
            </text>
          </g>
        </svg>
        <Tooltip title={tooltipTitle} items={tooltipItems} />
      </div>
    </div>
  );
};

export default RadialChart;
